import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Mail, MapPin, Phone } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { track } from "../lib/analytics";
import { Reveal } from "../components/Reveal";
import SeoHead from "../components/SeoHead";
import { OFFER_TYPES } from "../data/site";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Contact() {
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "", role: "",
    interest: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete your name, email, and message.");
      return;
    }
    if (!form.interest) {
      toast.error("Please select what you're interested in.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      track("form_submit", "/contact", form.interest);
      setDone(true);
      toast.success("Request received. We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-[72px]">
      <SeoHead
        title="Request an Assessment | Local Proof STL — St. Louis"
        description="Request an Operational Performance Assessment with Local Proof STL — an evidence-based view of operational health for established, owner-led service businesses in St. Louis."
        canonical="https://localproofstl.com/contact"
        ogType="website"
        ogUrl="https://localproofstl.com/contact"
        ogImage="https://localproofstl.com/og-image.jpg"
        twitterImage="https://localproofstl.com/og-image.jpg"
      />
      <div className="grid lg:grid-cols-2 min-h-[calc(100vh-72px)]">
        {/* Left — info */}
        <div className="bg-ink text-white relative overflow-hidden flex items-center">
          <div className="noise-overlay" />
          <div className="relative px-6 lg:px-16 py-20 lg:py-0 max-w-xl">
            <p className="eyebrow text-mist mb-8">Request an assessment</p>
            <h1 className="font-serif font-light text-5xl lg:text-6xl leading-[0.98] tracking-tight mb-8">
              Let&rsquo;s understand your operations.
            </h1>
            <p className="text-cloud/80 text-lg leading-relaxed mb-12">
              Tell us about your business. We&rsquo;ll review your request and follow up with
              next steps for an Operational Performance Assessment.
            </p>
            <ul className="space-y-5">
              <li className="flex items-center gap-4 text-cloud">
                <MapPin size={18} className="text-mist" /> St. Louis, Missouri
              </li>
              <li className="flex items-center gap-4 text-cloud">
                <Phone size={18} className="text-mist" />
                <a href="tel:+13142460787" className="hover:text-white transition-colors" data-testid="contact-phone-link">314-246-0787</a>
              </li>
              <li className="flex items-center gap-4 text-cloud">
                <Mail size={18} className="text-mist" />
                <a href="mailto:jay@localproofstl.com" className="hover:text-white transition-colors">jay@localproofstl.com</a>
              </li>
            </ul>
          </div>
        </div>

        {/* Right — form */}
        <div className="flex items-center px-6 lg:px-16 py-20">
          <div className="w-full max-w-lg">
            {done ? (
              <Reveal>
                <div data-testid="contact-success" className="text-center lg:text-left">
                  <div className="w-14 h-14 rounded-full bg-ink text-white flex items-center justify-center mb-6 mx-auto lg:mx-0">
                    <ArrowUpRight size={22} />
                  </div>
                  <h2 className="font-serif text-4xl text-ink mb-4">Request received.</h2>
                  <p className="text-charcoal/70 leading-relaxed">
                    Thank you, {form.name.split(" ")[0]}. We&rsquo;ll review your request and
                    follow up with next steps.
                  </p>
                </div>
              </Reveal>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name" className="text-charcoal">Name *</Label>
                    <Input id="name" data-testid="contact-name" value={form.name} onChange={set("name")} className="mt-2" placeholder="Jane Doe" />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-charcoal">Email *</Label>
                    <Input id="email" type="email" data-testid="contact-email" value={form.email} onChange={set("email")} className="mt-2" placeholder="jane@company.com" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="company" className="text-charcoal">Company</Label>
                    <Input id="company" data-testid="contact-company" value={form.company} onChange={set("company")} className="mt-2" placeholder="Acme Mechanical" />
                  </div>
                  <div>
                    <Label htmlFor="role" className="text-charcoal">Role</Label>
                    <Input id="role" data-testid="contact-role" value={form.role} onChange={set("role")} className="mt-2" placeholder="Owner / President" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="phone" className="text-charcoal">Phone (optional)</Label>
                  <Input id="phone" data-testid="contact-phone" value={form.phone} onChange={set("phone")} className="mt-2" placeholder="(314) 000-0000" />
                </div>
                <div>
                  <Label className="text-charcoal">Interested in *</Label>
                  <Select value={form.interest} onValueChange={(v) => setForm((f) => ({ ...f, interest: v }))}>
                    <SelectTrigger data-testid="contact-interest" className="mt-2">
                      <SelectValue placeholder="Select an offer type" />
                    </SelectTrigger>
                    <SelectContent>
                      {OFFER_TYPES.map((i) => (
                        <SelectItem key={i} value={i}>{i}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="message" className="text-charcoal">Message *</Label>
                  <Textarea id="message" data-testid="contact-message" value={form.message} onChange={set("message")} rows={4} className="mt-2" placeholder="Tell us about your business and what you'd like to improve." />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={submitting}
                  className="group w-full inline-flex items-center justify-center gap-2 bg-ink text-white px-7 py-4 rounded-full text-sm font-semibold hover:bg-charcoal transition-colors duration-200 disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Submit request"}
                  <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
